export enum GuideMainTopics {
  GET_STARTED = 'get-started',
  FUNDAMENTALS = 'fundamentals',
  METHODS = 'methods',
  INSIGHTS = 'insights',
  TUTORIALS = 'tutorials',
  DEMOS = 'demos',
  GLOSSARY = 'glossary',
}

export interface GuideMainTab {
  id: number
  documentId: string
  title: string
  description?: string | null
  slug: string
  order: number
  visible: boolean
  sub_tabs: GuideSubTab[]
  createdAt: Date
  updatedAt: Date
  publishedAt: Date
}

export interface GuideSubTab {
  id: number
  documentId: string
  title: string
  slug: string
  order: number
  visible: boolean
  createdAt: Date
  updatedAt: Date
  publishedAt: Date
  main_topic?: GuideMainTab | null
  articles?: Article[]
}

export interface HeroImage {
  id: number
  documentId: string
  url: string
  alternativeText: string | null
  formats: {
    small: {
      url: string
      hash: string
      ext: string
      mime: string
      name: string
      path: string
      size: number
      width: number
      height: number
      sizeInBytes: number
    }
    thumbnail: {
      url: string
      hash: string
      ext: string
      mime: string
      name: string
      path: string
      size: number
      width: number
      height: number
      sizeInBytes: number
    }
    medium: {
      url: string
      hash: string
      ext: string
      mime: string
      name: string
      path: string
      size: number
      width: number
      height: number
      sizeInBytes: number
    }
    large: {
      url: string
      hash: string
      ext: string
      mime: string
      name: string
      path: string
      size: number
      width: number
      height: number
      sizeInBytes: number
    }
  }
}

export interface Article {
  id: number
  documentId: string
  title: string
  slug: string
  description: string | null
  hero_image: HeroImage | null
  sub_tabs?: SubTab[]
  content_blocks?: ContentBlock[]
  authors?: Author[]
  createdAt: string
  updatedAt: string
  publishedAt: string
  is_external?: boolean
  external_link: string
}

export interface SocialLink {
  id: number
  order: number | null
  platform: string
  handle: string | null
  url: string
}

export interface Author {
  id: number
  documentId: string
  name: string
  email: string
  createdAt: string
  updatedAt: string
  publishedAt: string
  bio: string | null
  social_links: SocialLink[]
}

export interface ContentBlock {
  id: number
  __component?: string
  Title?: string
  body?: string
  Label?: string
  Url?: string
}

export interface MainTopic {
  id: number
  documentId: string
  title: string
  slug: string
  description: string
}

export interface SubTab {
  id: number
  documentId: string
  title: string
  slug: string
  main_topic: MainTopic
  articles: Article[]
}
export type MainTopicPayload = {
  id: number
  documentId: string
  title: string
  slug: string
  order: number
  visible: boolean
  createdAt: string
  updatedAt: string
  publishedAt: string
  main_topic: MainTopic
  articles: Article[]
}

export interface SubTabsResponse {
  data: SubTab[]
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

export interface StrapiResponse {
  meta: {
    pagination: {
      page: number
      pageSize: number
      pageCount: number
      total: number
    }
  }
}

export type StrapiSearchResult = {
  id: number
  documentId: string
  title: string
  slug: string
  read_time_minutes: number
  description: string | null
  sub_tabs: Array<{
    id: number
    documentId: string
    title: string
    slug: string
    main_topic: {
      id: number
      documentId: string
      title: string
      slug: string
    }
  }>
  type: 'article'
}

export interface SearchStrapiResponse extends StrapiResponse {
  data: StrapiSearchResult[]
}

export interface Demo {
  documentId: string
  title: string
  video: {
    url?: string
    privacyHash?: string
    providerUi?: string
    provider?: string
  }
  summary: string
  Slug: string
  thumbnail: {
    url: string
    alternativeText?: string
    caption?: string
    width?: number
    height?: number
    formats?: {
      [key: string]: {
        url: string
        width: number
        height: number
      }
    }
  } | null
  length: number | null
  sub_tabs: Array<{
    documentId: string
    title: string
    slug: string
  }>
}

export interface Tutorial {
  id: number
  documentId: string
  title: string
  slug: string
  thumbnail?: Media | null
  summary?: string
  arcadeLink?: string
  sub_tabs?: GuideSubTab[]
  createdAt: Date
  updatedAt: Date
  publishedAt: Date
}

export interface Media {
  id: number
  url: string
  name: string
  alternativeText?: string | null
  caption?: string | null
  width?: number
  height?: number
  formats?: {
    [key: string]: {
      url: string
      width: number
      height: number
    }
  }
  createdAt: Date
  updatedAt: Date
}

export interface GlossaryProps {
  title: string
  introduction: string
}

export type GlossaryDefinition = { term: string; definition: string }
export type GlossaryGroups = Record<string, GlossaryDefinition[]>

export type GlossaryTerm = {
  id: number
  documentId: string
  letter: string
  term: string
  slug: string
  definition: string
  plain_definition: string | null
  createdAt: string
  updatedAt: string
  publishedAt: string | null
}

export interface GlossaryPage {
  createdAt: Date
  documentId: string
  id: number
  publishedAt: Date
  updatedAt: Date
  title: string
  description: string
  hero?: {
    url: string
    alternativeText: string
  }
}

export type SearchableItem = StrapiSearchResult | GlossarySearchResult

export interface GlossarySearchResult {
  documentId: string
  term: string
  definition: string
  letter: string
  slug: string
  type: 'glossary'
}
